import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { HiFilter, HiChevronRight, HiCheck, HiX, HiSearch } from 'react-icons/hi';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface HierarchicalFilterDropdownProps {
  skills: SelectOption[];
  selectedSkills: (string | number)[];
  selectedWorkItemTypes: string[];
  onSkillsChange: (skills: (string | number)[]) => void;
  onWorkItemTypesChange: (types: string[]) => void;
  onApplyFilters?: (skills: (string | number)[], workItemTypes: string[]) => void;
  className?: string;
  onClose?: () => void;
}

export interface HierarchicalFilterDropdownRef {
  close: () => void;
}

const HierarchicalFilterDropdown = React.forwardRef<HierarchicalFilterDropdownRef, HierarchicalFilterDropdownProps>(({
  skills,
  selectedSkills,
  selectedWorkItemTypes,
  onSkillsChange,
  onWorkItemTypesChange,
  onApplyFilters,
  className = '',
  onClose,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [nestedDropdownPosition, setNestedDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Pending filter state - changes that haven't been applied yet
  const [pendingSkills, setPendingSkills] = useState<(string | number)[]>(selectedSkills);
  const [pendingWorkItemTypes, setPendingWorkItemTypes] = useState<string[]>(selectedWorkItemTypes);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nestedDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHoveredOption(null);
    setNestedDropdownPosition(null);
    setSearchTerm('');
    onClose?.();
  }, [onClose]);

  // Expose close method via ref
  React.useImperativeHandle(ref, () => ({
    close: closeDropdown,
  }), [closeDropdown]);

  // Sync pending state when props change (e.g., when filters are reset externally)
  useEffect(() => {
    setPendingSkills(selectedSkills);
    setPendingWorkItemTypes(selectedWorkItemTypes);
  }, [selectedSkills, selectedWorkItemTypes]);

  const workItemTypes = [
    { value: 'task', label: 'Task' },
    { value: 'user story', label: 'User Story' },
    { value: 'bug', label: 'Bug' },
    { value: 'spike', label: 'Spike' },
  ];

  const mainOptions = [
    { id: 'skills', label: 'Filter by skill', hasNested: true, options: skills },
    { id: 'workItemTypes', label: 'Filter by work item type', hasNested: true, options: workItemTypes },
  ];

  // Filter options based on search term (for both skills and work item types)
  const filteredOptions = useMemo(() => {
    if (!hoveredOption) return [];
    const activeOption = mainOptions.find(opt => opt.id === hoveredOption);
    if (!activeOption) return [];

    // Apply search filter if search term exists
    if (!searchTerm.trim()) {
      return activeOption.options;
    }

    const searchLower = searchTerm.toLowerCase();
    return activeOption.options.filter(option =>
      option.label.toLowerCase().includes(searchLower)
    );
  }, [hoveredOption, searchTerm, skills]);

  // Calculate filter count (using applied filters, not pending)
  const filterCount = selectedSkills.length + selectedWorkItemTypes.length;

  // Check if there are pending changes
  const hasPendingChanges = useMemo(() => {
    const skillsChanged = JSON.stringify([...pendingSkills].sort()) !== JSON.stringify([...selectedSkills].sort());
    const workItemTypesChanged = JSON.stringify([...pendingWorkItemTypes].sort()) !== JSON.stringify([...selectedWorkItemTypes].sort());
    return skillsChanged || workItemTypesChanged;
  }, [pendingSkills, pendingWorkItemTypes, selectedSkills, selectedWorkItemTypes]);

  // Close nested dropdown when parent closes
  useEffect(() => {
    if (!isOpen) {
      setHoveredOption(null);
      setNestedDropdownPosition(null);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (isOpen) {
        const clickedInsideButton = buttonRef.current?.contains(target);
        const clickedInsideDropdown = dropdownRef.current?.contains(target);
        const clickedInsideNested = nestedDropdownRef.current?.contains(target);
        
        if (!clickedInsideButton && !clickedInsideDropdown && !clickedInsideNested) {
          closeDropdown();
        }
      }
    };

    // Use click event instead of mousedown for better compatibility
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [isOpen]);

  // Close dropdown when other filters open (listen for clicks on other dropdowns)
  useEffect(() => {
    const handleOtherDropdownOpen = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is on another dropdown (CompactSelect, FilterSelect, etc.)
      if (isOpen && target) {
        const isOtherDropdown = target.closest('.relative') && 
          !dropdownRef.current?.contains(target) &&
          !buttonRef.current?.contains(target) &&
          !nestedDropdownRef.current?.contains(target) &&
          (target.closest('[role="listbox"]') || 
           target.closest('.absolute.top-full') ||
           target.closest('button[aria-expanded="true"]'));
        
        if (isOtherDropdown) {
          closeDropdown();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleOtherDropdownOpen, true);
      return () => document.removeEventListener('click', handleOtherDropdownOpen, true);
    }
  }, [isOpen]);

  // Focus search input when nested dropdown opens (only for skills)
  useEffect(() => {
    if (hoveredOption === 'skills' && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [hoveredOption]);

  // Calculate nested dropdown position
  useEffect(() => {
    if (hoveredOption) {
      const menuItem = menuItemRefs.current[hoveredOption];
      if (menuItem) {
        requestAnimationFrame(() => {
          const menuItemRect = menuItem.getBoundingClientRect();
          
          const left = menuItemRect.right + 8;
          const top = menuItemRect.top;

          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          const estimatedWidth = 280;
          const estimatedHeight = 400;
          
          let adjustedLeft = left;
          let adjustedTop = top;

          if (left + estimatedWidth > viewportWidth) {
            adjustedLeft = menuItemRect.left - estimatedWidth - 8;
            if (adjustedLeft < 8) {
              adjustedLeft = 8;
            }
          }

          if (top + estimatedHeight > viewportHeight) {
            adjustedTop = viewportHeight - estimatedHeight - 8;
            if (adjustedTop < 8) {
              adjustedTop = 8;
            }
          }

          setNestedDropdownPosition({ top: adjustedTop, left: adjustedLeft });
        });
      }
    } else {
      setNestedDropdownPosition(null);
      setSearchTerm('');
    }
  }, [hoveredOption]);

  const handleMainOptionHover = (optionId: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredOption(optionId);
    setSearchTerm('');
  };

  const handleMainOptionLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (nestedDropdownRef.current && document.elementFromPoint) {
        const elementAtMouse = document.elementFromPoint(
          (window as any).lastMouseX || 0,
          (window as any).lastMouseY || 0
        );
        if (
          !nestedDropdownRef.current.contains(elementAtMouse) &&
          !dropdownRef.current?.contains(elementAtMouse)
        ) {
          setHoveredOption(null);
          setNestedDropdownPosition(null);
          setSearchTerm('');
        }
      } else {
        setHoveredOption(null);
        setNestedDropdownPosition(null);
        setSearchTerm('');
      }
    }, 150);
  };

  const handleNestedHover = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleNestedLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredOption(null);
    setNestedDropdownPosition(null);
    setSearchTerm('');
  };

  // Track mouse position for better hover detection
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      (window as any).lastMouseX = e.clientX;
      (window as any).lastMouseY = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const toggleSkill = (skillValue: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSkills = pendingSkills.includes(skillValue)
      ? pendingSkills.filter(s => s !== skillValue)
      : [...pendingSkills, skillValue];
    setPendingSkills(newSkills);
  };

  const toggleWorkItemType = (type: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newTypes = pendingWorkItemTypes.includes(type)
      ? pendingWorkItemTypes.filter(t => t !== type)
      : [...pendingWorkItemTypes, type];
    setPendingWorkItemTypes(newTypes);
  };

  const handleSelectAllSkills = (e: React.MouseEvent) => {
    e.stopPropagation();
    const allSkillValues = filteredOptions.map(opt => opt.value);
    setPendingSkills(allSkillValues);
  };

  const handleClearSkills = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingSkills([]);
  };

  const handleSelectAllWorkItemTypes = (e: React.MouseEvent) => {
    e.stopPropagation();
    const allWorkItemTypeValues = filteredOptions.map(opt => opt.value as string);
    setPendingWorkItemTypes(allWorkItemTypeValues);
  };

  const handleClearWorkItemTypes = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingWorkItemTypes([]);
  };

  const handleApplyFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If onApplyFilters is provided, use it (for warning modal integration)
    // Otherwise, use the individual callbacks
    if (onApplyFilters) {
      onApplyFilters(pendingSkills, pendingWorkItemTypes);
    } else {
      onSkillsChange(pendingSkills);
      onWorkItemTypesChange(pendingWorkItemTypes);
    }
  };

  const handleCancelChanges = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingSkills(selectedSkills);
    setPendingWorkItemTypes(selectedWorkItemTypes);
  };

  const hasActiveFilters = filterCount > 0;

  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium
            bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 rounded-lg
            hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-tertiary-dark
            focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600
            text-gray-900 dark:text-white transition-all duration-200 cursor-pointer
            ${hasActiveFilters ? 'ring-1 ring-gray-400 dark:ring-gray-500' : ''}
            ${isOpen ? 'ring-1 ring-gray-400 dark:ring-gray-500' : ''}`}
          aria-label="Sprint filters"
        >
          <HiFilter className="w-4 h-4" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="ml-1 w-5 h-5 flex items-center justify-center text-xs font-semibold 
              bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 
              border border-gray-300 dark:border-gray-600 rounded-full">
              {filterCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div
            className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-secondary-dark
              border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-[9998]
              overflow-hidden"
          >
            {mainOptions.map((option) => {
              const isHovered = hoveredOption === option.id;
              const isSkillsOption = option.id === 'skills';
              const isWorkItemTypesOption = option.id === 'workItemTypes';
              const hasSelection = isSkillsOption
                ? selectedSkills.length > 0
                : isWorkItemTypesOption
                ? selectedWorkItemTypes.length > 0
                : false;

              return (
                <div
                  key={option.id}
                  ref={(el) => {
                    menuItemRefs.current[option.id] = el;
                  }}
                  className={`relative px-4 py-2.5 text-sm cursor-pointer transition-all duration-150
                    ${isHovered
                      ? 'bg-gray-100 dark:bg-tertiary-dark'
                      : hasSelection
                      ? 'bg-gray-50 dark:bg-secondary-dark/50'
                      : ''}
                    ${!isHovered ? 'hover:bg-gray-100 dark:hover:bg-tertiary-dark' : ''}`}
                  onMouseEnter={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    handleMainOptionHover(option.id);
                  }}
                  onMouseLeave={handleMainOptionLeave}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-white font-medium">{option.label}</span>
                    <div className="flex items-center gap-2">
                      {hasSelection && (
                        <span className="w-5 h-5 flex items-center justify-center text-xs font-semibold 
                          bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 
                          border border-gray-300 dark:border-gray-600 rounded-full">
                          {isSkillsOption ? selectedSkills.length : selectedWorkItemTypes.length}
                        </span>
                      )}
                      <HiChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isHovered ? 'translate-x-0.5' : ''}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Nested Dropdown - Rendered via Portal */}
      {hoveredOption && nestedDropdownPosition && typeof document !== 'undefined' && (
        createPortal(
          <div
            ref={nestedDropdownRef}
            className="fixed bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl z-[9999] overflow-hidden w-[280px]"
            style={{
              top: `${nestedDropdownPosition.top}px`,
              left: `${nestedDropdownPosition.left}px`,
            }}
            onMouseEnter={handleNestedHover}
            onMouseLeave={handleNestedLeave}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input - For both skills and work item types */}
            {(hoveredOption === 'skills' || hoveredOption === 'workItemTypes') && (
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <HiSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={hoveredOption === 'skills' ? 'Search skills...' : 'Search work item types...'}
                    className="w-full pl-8 pr-7 py-1.5 text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded
                      focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500
                      focus:border-gray-400 dark:focus:border-gray-500
                      text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm('');
                      }}
                      className="absolute right-1.5 top-1/2 transform -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                    >
                      <HiX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Select All / Clear - For both skills and work item types */}
            {(hoveredOption === 'skills' || hoveredOption === 'workItemTypes') && filteredOptions.length > 1 && (
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-tertiary-dark">
                <div className="flex justify-between w-full">
                  <button
                    type="button"
                    onClick={hoveredOption === 'skills' ? handleSelectAllSkills : handleSelectAllWorkItemTypes}
                    className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200"
                  >
                    Select All ({filteredOptions.length})
                  </button>
                  <button
                    type="button"
                    onClick={hoveredOption === 'skills' ? handleClearSkills : handleClearWorkItemTypes}
                    className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Options List - Scrollable */}
            <div className={`overflow-y-auto ${hasPendingChanges ? 'max-h-[240px]' : 'max-h-[320px]'}`}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSkillsOption = hoveredOption === 'skills';
                  const isSelected = isSkillsOption
                    ? pendingSkills.includes(option.value)
                    : pendingWorkItemTypes.includes(option.value as string);

                  return (
                    <div
                      key={option.value}
                      className={`px-4 py-2.5 text-sm cursor-pointer transition-all duration-150 flex items-center justify-between group
                        ${isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500 dark:border-blue-400'
                          : 'hover:bg-gray-50 dark:hover:bg-tertiary-dark border-l-2 border-transparent'}`}
                      onClick={(e) => {
                        if (isSkillsOption) {
                          toggleSkill(option.value, e);
                        } else {
                          toggleWorkItemType(option.value as string, e);
                        }
                      }}
                    >
                      <span className={`text-gray-900 dark:text-white ${isSelected ? 'font-medium' : ''}`}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <HiCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No {hoveredOption === 'skills' ? 'skills' : 'work item types'} found
                </div>
              )}
            </div>

            {/* Apply Filters Footer - Only show when there are pending changes */}
            {hasPendingChanges && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 flex items-center justify-between gap-2">
                <button
                  onClick={handleCancelChanges}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 
                    bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 
                    rounded-md hover:bg-gray-50 dark:hover:bg-tertiary-dark 
                    transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white 
                    bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 
                    rounded-md transition-colors duration-150 shadow-sm"
                >
                  Apply Filters
                </button>
              </div>
            )}
          </div>,
          document.body
        )
      )}
    </>
  );
});

HierarchicalFilterDropdown.displayName = 'HierarchicalFilterDropdown';

export default HierarchicalFilterDropdown;